import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, ChatBubbleLeftRightIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission - replace with actual API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Message sent! We\'ll get back to you within 24-48 hours.');
    setFormData({ name: '', email: '', subject: '', message: '' });
    setLoading(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900">Contact & Support</h1>
        <p className="mt-4 text-lg text-gray-600">
          Have questions? We're here to help.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        {/* Email Support */}
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 mb-4">
            <EnvelopeIcon className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Support</h3>
          <p className="text-gray-600 mb-4">For general inquiries and support</p>
          <a href="mailto:support@d-espial.com" className="text-blue-600 hover:text-blue-500 font-medium">
            support@d-espial.com
          </a>
        </div>

        {/* Billing */}
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100 mb-4">
            <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Billing Support</h3>
          <p className="text-gray-600 mb-4">For subscription and payment questions</p>
          <a href="mailto:billing@d-espial.com" className="text-blue-600 hover:text-blue-500 font-medium">
            billing@d-espial.com
          </a>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-purple-100 mb-4">
            <QuestionMarkCircleIcon className="h-6 w-6 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Quick Help</h3>
          <p className="text-gray-600 mb-4">Common questions answered</p>
          <Link to="/terms" className="text-blue-600 hover:text-blue-500 font-medium">
            View Terms & Policies
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Contact Form */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Send us a Message</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Your Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject *
              </label>
              <select
                id="subject"
                name="subject"
                required
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a subject</option>
                <option value="general">General Inquiry</option>
                <option value="subscription">Subscription Question</option>
                <option value="billing">Billing Issue</option>
                <option value="refund">Refund Request</option>
                <option value="technical">Technical Support</option>
                <option value="feedback">Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="How can we help you?"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        {/* FAQ Section */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-medium text-gray-900 mb-2">How do I cancel my subscription?</h3>
              <p className="text-gray-600 text-sm">
                You can cancel your subscription anytime from your <Link to="/profile" className="text-blue-600 hover:text-blue-500">account settings</Link>. 
                Your access will continue until the end of your current billing period.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-medium text-gray-900 mb-2">Can I get a refund?</h3>
              <p className="text-gray-600 text-sm">
                First-time subscribers can request a full refund within 7 days. For other situations, 
                please review our <Link to="/terms" className="text-blue-600 hover:text-blue-500">refund policy</Link> or contact us.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-medium text-gray-900 mb-2">How do I upgrade my plan?</h3>
              <p className="text-gray-600 text-sm">
                Visit your <Link to="/profile" className="text-blue-600 hover:text-blue-500">account settings</Link> and 
                click "Manage Subscription" to upgrade to a higher tier. Changes take effect immediately.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-medium text-gray-900 mb-2">Is my payment information secure?</h3>
              <p className="text-gray-600 text-sm">
                Yes! We use Stripe, a PCI Level 1 certified payment processor. Your payment details are 
                never stored on our servers. Learn more in our <Link to="/privacy" className="text-blue-600 hover:text-blue-500">privacy policy</Link>.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-medium text-gray-900 mb-2">What's your response time?</h3>
              <p className="text-gray-600 text-sm">
                We typically respond to support requests within 24-48 hours. Diamond plan subscribers 
                receive priority support with faster response times.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="mt-12 pt-8 border-t border-gray-200 text-center">
        <div className="flex justify-center space-x-6">
          <Link to="/terms" className="text-gray-600 hover:text-gray-900 text-sm">Terms of Service</Link>
          <Link to="/privacy" className="text-gray-600 hover:text-gray-900 text-sm">Privacy Policy</Link>
          <Link to="/subscription" className="text-gray-600 hover:text-gray-900 text-sm">Subscription Plans</Link>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
